import React, {useContext, useEffect, useRef, useState} from "react";
import {NavigationContext} from "../common/NavigationContext";
import {UserContext} from "../common/UserContext";
import Brands from "./Brands";
import {AdminSettingsIcon, GoIcon, LogoutIcon, PinIcon, SearchIcon, SettingsIcon, UserIcon} from "../common/icons";
import AdminSchoolSelector from "./AdminSchoolSelector";
import ActForUser from "./ActForUser";
import ShortcutLink from "../common/ShortcutLink";
import {Globe} from "react-feather";
import {ShortcutContext} from "../common/ShortcutContext";
import useCurrentShortcut from "../hooks/useCurrentShortcut";
import UserReview from "../review/UserReview";
import StudentDiagnosticMenuWidget from "../diagnosticReporting/StudentDiagnosticMenuWidget";
import PromptSearch from "./PromptSearch";
import CMSSearch from "../cms/CMSSearch";
import {ArrowBack} from "@mui/icons-material";
import NavAccessibilityWrap from "./NavAccessibilityWrap";
import {BrandContext} from "../common/BrandContext";

const SmallScreenSettings = () => {
    const [selectedShortcut, setSelectedShortcut] = useState(null);
    const settingsNavigationNames = ['Brands', 'Admin settings', 'Search Prompt', 'CMS Search', 'Settings', 'Logout', 'Test follow-up work', 'Return to GO'];
    const [settingsNavigationShortcuts, setSettingsNavigationShortcuts] = useState(null);
    const {setNavVisible} = useContext(NavigationContext);
    const { user } = useContext(UserContext);
    const { globalShortcuts } = useContext(ShortcutContext);
    const brandConfig = useContext(BrandContext);
    const { loadShortcut } = useCurrentShortcut();
    const focusedComponentRef = useRef(null);
    const firstShortcutButtonRef = useRef(null);

    useEffect(() => {
        if(globalShortcuts){
            setSettingsNavigationShortcuts(
                settingsNavigationNames.reduce((acc, name) => {
                    const shortcut = globalShortcuts.find(item => item.name === name);
                    if (shortcut) {
                        if (shortcut.actForUser && user.actForUserId === undefined) {
                            return acc;
                        } else if (shortcut.multiBrand && (user.brandCodes === undefined || user.brandCodes.length <= 1)) {
                            return acc;
                        } else if (shortcut.adminShortcut && !brandConfig.enableAdminShortcuts && !user.adminUser && user.actForUserId === undefined) {
                            return acc;
                        }
                        if (
                            (shortcut.shortcutId == 153 && user.authorityId != 2) ||
                            (shortcut.shortcutId == 177 && !user.testResultId)
                        ) {
                            return acc;
                        }
                        if (shortcut.childShortcuts.length > 0) {
                            shortcut.childShortcuts.forEach(child => {
                                if (!child.iconComponent) {
                                    child.iconComponent = child.name
                                        .replace(/\s+/g, ' ')
                                        .replace(/\b\w/g, char => char.toUpperCase())
                                        .replace(/\s+/g, '') + 'Icon';
                                }
                            });
                            acc.push(...shortcut.childShortcuts);
                        } else {
                            acc.push(shortcut);
                        }
                    }

                    return acc;
                }, [])
            )
        }
    }, [user, globalShortcuts]);

    const menuComponents = {
        Brands: Brands,
        AdminSchoolSelector: AdminSchoolSelector,
        ActForUser: ActForUser,
        UserReview: UserReview,
        StudentDiagnosticMenuWidget: StudentDiagnosticMenuWidget,
        PromptSearch: PromptSearch,
        CMSSearch: CMSSearch,
    }

    const iconComponents = {
        SettingsIcon: SettingsIcon,
        AdminSchoolSelectorIcon: AdminSettingsIcon,
        ClearActForUserIcon: UserIcon,
        AccountSettingsIcon: UserIcon,
        TimeZoneIcon: Globe,
        LogoutIcon: LogoutIcon,
        GoIcon: GoIcon,
        PinIcon: PinIcon,
        SearchIcon: SearchIcon
    }

    const handleClick = (shortcut) => {
        if(shortcut.menuComponent) setSelectedShortcut(shortcut);
        loadShortcut(shortcut);
        if(shortcut.url || shortcut.loadFunction) setNavVisible(false);
    }

    useEffect(() => {
        if (selectedShortcut && focusedComponentRef.current) {
            focusedComponentRef.current.focus();
        }
        if (!selectedShortcut && firstShortcutButtonRef.current) {
            firstShortcutButtonRef.current.focus();
        }
    }, [selectedShortcut]);

    return(
        <>
            {!selectedShortcut &&
                <>
                    <ul className={'ch-nav-settings-list'}>
                        {settingsNavigationShortcuts?.map((item, index) => {
                            if(!user.actForUserId && item.name === 'Clear act for user') return;
                            if(brandConfig.brandCode === 'USA' && item.name === 'Time zone') return;

                            const isFirstFocusable = index === 0;
                            return(
                                <li key={`nav-item-${item.name}`}>
                                    <button
                                        ref={isFirstFocusable ? firstShortcutButtonRef : null}
                                        className={'ch-nav-settings-button'}
                                        onClick={() => handleClick(item)}
                                    >
                                        <div className={'ch-nav-settings-icon'} aria-hidden={'true'}>
                                            {React.createElement(iconComponents[item.iconComponent])}
                                        </div>
                                        {item.name}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </>
            }
            {selectedShortcut &&
                <>
                    <button className={'ch-nav-settings-button'} onClick={() => setSelectedShortcut(null)}>
                        <div className={'ch-nav-settings-icon'}><ArrowBack /></div>
                        Back
                    </button>
                    <p className={'ch-nav-settings-heading'}>{selectedShortcut.name}</p>
                    <NavAccessibilityWrap aria-label={"Settings Navigation"}>
                        <div ref={focusedComponentRef} tabIndex={-1}>
                            {React.createElement(menuComponents[selectedShortcut.menuComponent])}
                        </div>
                    </NavAccessibilityWrap>
                </>
            }
        </>
    )
}

export default SmallScreenSettings;